export const CASE_STATUS_VALUES = [
  'hidden',
  'investigating',
  'truth_suspected',
  'unresolved',
  'misdirected'
] as const;

export type CaseStatus = (typeof CASE_STATUS_VALUES)[number];

export const CHARACTER_POSITION_VALUES = ['left', 'center', 'right'] as const;

export type CharacterPosition = (typeof CHARACTER_POSITION_VALUES)[number];

export const BACKGROUND_EFFECT_VALUES = [
  'none',
  'zoomIn',
  'zoomOut',
  'slowPan',
  'subtleDrift',
  'shake',
  'glitch',
  'rain',
  'noise',
  'staticNoise',
  'darken'
] as const;

export type BackgroundEffect = (typeof BACKGROUND_EFFECT_VALUES)[number];

export const BACKGROUND_TRANSITION_VALUES = [
  'none',
  'cut',
  'fade',
  'whiteFlash',
  'blackFade'
] as const;

export type BackgroundTransition = (typeof BACKGROUND_TRANSITION_VALUES)[number];

export const SCENARIO_ACTION_TYPE_VALUES = [
  'addEvidence',
  'updateCaseStatus',
  'playSe',
  'screenShake',
  'fade',
  'goToTitle'
] as const;

export type ScenarioActionType = (typeof SCENARIO_ACTION_TYPE_VALUES)[number];

export const AUDIO_TRACK_CHANNEL_VALUES = ['bgm', 'se', 'voice'] as const;

export type AudioTrackChannel = (typeof AUDIO_TRACK_CHANNEL_VALUES)[number];

export const AUDIO_SOURCE_TYPE_VALUES = [
  'procedural_placeholder',
  'original',
  'licensed',
  'silent_placeholder'
] as const;

export type AudioSourceType = (typeof AUDIO_SOURCE_TYPE_VALUES)[number];

export const CASE_ENDING_TYPE_VALUES = [
  'unresolved',
  'truth_suspected',
  'misdirected',
  'false_accusation_prevented',
  'damage_contained',
  'damage_expanded'
] as const;

export type CaseEndingType = (typeof CASE_ENDING_TYPE_VALUES)[number];

export const INVESTIGATION_REVISIT_POLICY_VALUES = [
  'always',
  'once_then_summary',
  'after_new_evidence'
] as const;

export type InvestigationRevisitPolicy = (typeof INVESTIGATION_REVISIT_POLICY_VALUES)[number];

export const INVESTIGATION_POINT_OUTCOME_VALUES = [
  'flavor',
  'core_evidence',
  'optional_evidence',
  'route_hint',
  'truth_hint',
  'joke'
] as const;

export type InvestigationPointOutcome = (typeof INVESTIGATION_POINT_OUTCOME_VALUES)[number];

export type InvestigationUnlockConditionType =
  | 'always'
  | 'after_point'
  | 'after_evidence'
  | 'after_location';

export type DeductionMode = 'practice' | 'case_main' | 'optional';

export type DeductionUiLayout = 'evidence_strip' | 'evidence_grid' | 'compact_mobile_stack';

export type DeductionCompletionAction = 'return_to_novel' | 'open_choice' | 'open_case_list';

export type DeductionCaseStatusPolicy =
  | 'do_not_solve'
  | 'truth_suspected_only'
  | 'no_status_change';

export type TestimonyUiLayout = 'interview_cards' | 'transcript_panel' | 'compact_mobile_stack';

export type TestimonyFocusFailPolicy = 'retry_current' | 'soft_fail_summary' | 'return_to_novel';

export type TestimonyWitnessStance =
  | 'friendly'
  | 'guarded'
  | 'confused'
  | 'panicked'
  | 'competitive'
  | 'hostile';

export type TestimonyStatementOutcome =
  | 'context'
  | 'contradiction'
  | 'truth_hint'
  | 'rumor_noise'
  | 'joke';

export type TestimonyCompletionAction = 'return_to_novel' | 'open_choice' | 'open_case_list';

export type TestimonyCaseStatusPolicy =
  | 'do_not_solve'
  | 'truth_suspected_only'
  | 'no_status_change';

export type ReasoningBoardUiLayout = 'evidence_board' | 'timeline_links' | 'compact_mobile_stack';

export type ReasoningBoardCardType =
  | 'evidence'
  | 'testimony_statement'
  | 'deduction_step'
  | 'character'
  | 'location'
  | 'time_marker'
  | 'hypothesis';

export type ReasoningBoardConnectionType =
  | 'supports'
  | 'contradicts'
  | 'points_to'
  | 'occurs_before'
  | 'same_source'
  | 'red_herring';

export type ReasoningBoardSlotType = 'single_card' | 'ordered_cards' | 'connection';

export type ReasoningBoardCompletionAction = 'return_to_novel' | 'open_choice' | 'open_case_list';

export type ReasoningBoardCaseStatusPolicy =
  | 'do_not_solve'
  | 'truth_suspected_only'
  | 'no_status_change';

export type TownAnxietyUiLayout = 'meter_strip' | 'header_icon' | 'bulletin_overlay';

export type TownAnxietyTone = 'watchful' | 'rattled' | 'panic' | 'distorted';

export type TownAnxietyTriggerType =
  | 'scenario_node'
  | 'evidence'
  | 'testimony_statement'
  | 'deduction_step'
  | 'reasoning_slot'
  | 'case_status'
  | 'manual';

export type TownAnxietyReferenceTriggerType = Exclude<
  TownAnxietyTriggerType,
  'case_status' | 'manual'
>;

export type TownAnxietyStoragePolicy = 'case_progress' | 'global_progress';

export type UnresolvedLogUiLayout = 'timeline_archive' | 'case_file_tabs' | 'compact_mobile_stack';

export type UnresolvedLogTimelineType =
  | 'incident'
  | 'evidence'
  | 'testimony'
  | 'reasoning'
  | 'town_reaction'
  | 'unresolved_registered';

export type UnresolvedLogRelatedCaseRelation =
  | 'same_location'
  | 'same_character'
  | 'same_motif'
  | 'followup'
  | 'rumor_only';

export type UnresolvedLogCaseStatusPolicy =
  | 'truth_suspected_only'
  | 'unresolved_only'
  | 'no_status_change';

export type UnresolvedLogDefaultSort = 'timeline' | 'display_order' | 'latest_first';

export type CharacterRole =
  | 'protagonist'
  | 'detective'
  | 'heroine'
  | 'support'
  | 'police'
  | 'rival'
  | 'phantom_thief'
  | 'suspect'
  | 'victim'
  | 'resident'
  | 'narrator';

export type BackgroundCategory =
  | 'town'
  | 'detective_police'
  | 'crime_scene'
  | 'daily'
  | 'ominous'
  | 'transition'
  | 'ui';

export type ScenarioLineEffect =
  | 'none'
  | 'shake'
  | 'glitch'
  | 'noise'
  | 'stamp'
  | 'flash'
  | 'konaiArrival'
  | 'policeCall'
  | 'clockReverse'
  | 'caseFileTransition'
  | 'thiefNotice'
  | 'thiefFail';

export type FadeDirection = 'in' | 'out' | 'inOut';

export interface ScenarioLineEffectOptions {
  duration?: number;
  inputGuard?: number;
  settle?: number;
  fade?: number;
  shakeIntensity?: number;
  shakeDuration?: number;
  label?: string;
  title?: string;
  subtitle?: string;
  kicker?: string;
  caseTitle?: string;
  statusText?: string;
  stampText?: string;
}

export interface BackgroundCue {
  id: string;
  transition?: BackgroundTransition;
  duration?: number;
  effect?: BackgroundEffect;
  effectIntensity?: number;
}

export interface ScenarioCharacterCue {
  id: string;
  expression?: string;
  position: CharacterPosition;
  visible?: boolean;
  offsetY?: number;
  frame?: 'photo';
}

export interface ScenarioLine {
  id?: string;
  speaker?: string;
  speakerId?: string;
  text: string;
  characters?: ScenarioCharacterCue[];
  highlights?: string[];
  ruby?: ScenarioRuby[];
  se?: string;
  voice?: string;
  effect?: ScenarioLineEffect;
  effectOptions?: ScenarioLineEffectOptions;
}

export interface ScenarioRuby {
  base: string;
  reading: string;
  occurrence?: number;
}

export interface ScenarioChoice {
  id?: string;
  text: string;
  next: string;
  actions?: ScenarioAction[];
}

export type ScenarioAction =
  | AddEvidenceAction
  | UpdateCaseStatusAction
  | PlaySeAction
  | ScreenShakeAction
  | FadeAction
  | GoToTitleAction;

export interface AddEvidenceAction {
  type: 'addEvidence';
  id: string;
}

export interface UpdateCaseStatusAction {
  type: 'updateCaseStatus';
  id: string;
  status: CaseStatus;
}

export interface PlaySeAction {
  type: 'playSe';
  id: string;
}

export interface ScreenShakeAction {
  type: 'screenShake';
  intensity?: number;
  duration?: number;
}

export interface FadeAction {
  type: 'fade';
  direction?: FadeDirection;
  color?: string;
  duration?: number;
}

export interface GoToTitleAction {
  type: 'goToTitle';
}

export interface ScenarioNode {
  id: string;
  background?: BackgroundCue;
  bgm?: string | null;
  characters?: ScenarioCharacterCue[];
  lines: ScenarioLine[];
  choices?: ScenarioChoice[];
  actions?: ScenarioAction[];
  next?: string | null;
}

export interface ScenarioDocument {
  id: string;
  caseId: string;
  title: string;
  startNodeId: string;
  nodes: ScenarioNode[];
}

export interface CharacterExpressionDefinition {
  id: string;
  label: string;
  fileName?: string;
  assetPath?: string;
  unlock?: 'finalCaseComplete';
}

export interface CharacterDefinition {
  id: string;
  displayName: string;
  fullName?: string;
  role: CharacterRole;
  age?: number;
  profile?: string;
  defaultExpression: string;
  expressions: CharacterExpressionDefinition[];
}

export interface BackgroundPlaceholderDefinition {
  label?: string;
  colors?: [string, string];
}

export interface BackgroundDefinition {
  id: string;
  label: string;
  category: BackgroundCategory;
  assetPath?: string;
  defaultEffect?: BackgroundEffect;
  placeholder?: BackgroundPlaceholderDefinition;
  tags?: string[];
}

export interface AudioTrackDefinition {
  id: string;
  channel: AudioTrackChannel;
  label: string;
  description: string;
  fileName?: string;
  assetPath?: string;
  loop?: boolean;
  defaultVolume?: number;
  sourceType: AudioSourceType;
  license: string;
  credit?: string;
  tags?: string[];
}

export interface EvidenceDefinition {
  id: string;
  caseId: string;
  name: string;
  shortName?: string;
  description: string;
  detail?: string;
  iconId?: string;
  imageId?: string;
  imageAssetPath?: string;
  acquiredAtNodeId?: string;
  initiallyHidden?: boolean;
}

export interface CaseDefinition {
  id: string;
  title: string;
  subtitle?: string;
  summary: string;
  status: CaseStatus;
  firstScenarioId: string;
  evidenceIds: string[];
  displayOrder: number;
  unresolvedLog?: string;
  truthHint?: string;
  endingType?: CaseEndingType;
}

export interface InvestigationUnlockCondition {
  type: InvestigationUnlockConditionType;
  id?: string;
}

export interface InvestigationLocationDefinition {
  id: string;
  label: string;
  backgroundId: string;
  summary: string;
  revisitPolicy: InvestigationRevisitPolicy;
  dangerLevel?: number;
  displayOrder: number;
  tags?: string[];
}

export interface InvestigationPointDefinition {
  id: string;
  locationId: string;
  label: string;
  prompt: string;
  resultText: string;
  repeatText?: string;
  outcome: InvestigationPointOutcome;
  evidenceId?: string;
  requiredForProgress: boolean;
  unlockCondition?: InvestigationUnlockCondition;
  displayOrder: number;
  tags?: string[];
}

export interface InvestigationProgressionDefinition {
  minimumCheckedPoints: number;
  criticalEvidenceIds: string[];
  allowEarlyReturn: boolean;
  fallbackOnMissingCriticalEvidence?: 'none' | 'auto_acquire_in_main_route' | 'warn_and_continue';
}

export interface InvestigationDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  entryNodeId: string;
  returnNodeId: string;
  defaultLocationId: string;
  locations: InvestigationLocationDefinition[];
  points: InvestigationPointDefinition[];
  progression: InvestigationProgressionDefinition;
}

export interface DeductionUiDefinition {
  layout: DeductionUiLayout;
  allowRetry: boolean;
  showObservationTag: boolean;
  showLeapTag: boolean;
  wrongLimit?: number;
}

export interface DeductionReactionDefinition {
  speakerId: string;
  text: string;
  se?: string;
}

export interface DeductionStepDefinition {
  id: string;
  speakerId: string;
  claimText: string;
  observationText: string;
  leapText: string;
  correctEvidenceId: string;
  acceptedEvidenceIds?: string[];
  requiredEvidenceIds: string[];
  truthPressure: number;
  requiredForCompletion: boolean;
  correctReaction: DeductionReactionDefinition;
  wrongReactionText: string;
  skipIfMissingEvidence?: boolean;
  displayOrder: number;
  tags?: string[];
}

export interface DeductionCompletionDefinition {
  onComplete: DeductionCompletionAction;
  summaryText: string;
  caseStatusPolicy: DeductionCaseStatusPolicy;
}

export interface DeductionDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  entryNodeId: string;
  returnNodeId: string;
  mode: DeductionMode;
  ui: DeductionUiDefinition;
  steps: DeductionStepDefinition[];
  completion: DeductionCompletionDefinition;
}

export interface TestimonyUiDefinition {
  layout: TestimonyUiLayout;
  allowRetry: boolean;
  showFocusGauge: boolean;
  showPressButton: boolean;
  showEvidenceButton: boolean;
}

export interface TestimonyFocusDefinition {
  maxFocus: number;
  initialFocus: number;
  pressCost: number;
  wrongEvidenceCost: number;
  recoverOnCorrect: number;
  failPolicy: TestimonyFocusFailPolicy;
}

export interface TestimonyWitnessDefinition {
  id: string;
  characterId: string;
  label: string;
  stance: TestimonyWitnessStance;
  summary: string;
  displayOrder: number;
  tags?: string[];
}

export interface TestimonyReactionDefinition {
  speakerId: string;
  text: string;
  se?: string;
}

export interface TestimonyStatementDefinition {
  id: string;
  witnessId: string;
  statementText: string;
  pressText: string;
  contradictionHint: string;
  correctEvidenceId?: string;
  acceptedEvidenceIds?: string[];
  requiredEvidenceIds: string[];
  truthPressure: number;
  outcome: TestimonyStatementOutcome;
  requiredForCompletion: boolean;
  correctReaction: TestimonyReactionDefinition;
  wrongReactionText: string;
  skipIfMissingEvidence: boolean;
  displayOrder: number;
  tags?: string[];
}

export interface TestimonyCompletionDefinition {
  minimumContradictions: number;
  requiredStatementIds: string[];
  onComplete: TestimonyCompletionAction;
  summaryText: string;
  caseStatusPolicy: TestimonyCaseStatusPolicy;
}

export interface TestimonyDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  entryNodeId: string;
  returnNodeId: string;
  defaultWitnessId: string;
  ui: TestimonyUiDefinition;
  focus: TestimonyFocusDefinition;
  witnesses: TestimonyWitnessDefinition[];
  statements: TestimonyStatementDefinition[];
  completion: TestimonyCompletionDefinition;
}

export interface ReasoningBoardUiDefinition {
  layout: ReasoningBoardUiLayout;
  allowPartialScore: boolean;
  showTruthPressure: boolean;
  showUnresolvedStampPreview: boolean;
}

export interface ReasoningBoardCardDefinition {
  id: string;
  type: ReasoningBoardCardType;
  referenceId?: string;
  label: string;
  summary: string;
  displayOrder: number;
  tags?: string[];
}

export interface ReasoningBoardConnectionDefinition {
  id: string;
  fromCardId: string;
  toCardId: string;
  type: ReasoningBoardConnectionType;
  label: string;
  truthPressure: number;
  requiredForCore: boolean;
  displayOrder: number;
  tags?: string[];
}

export interface ReasoningBoardSlotBaseDefinition {
  id: string;
  label: string;
  prompt: string;
  requiredForCompletion: boolean;
  partialCredit: number;
  displayOrder: number;
  tags?: string[];
}

export interface ReasoningBoardSingleCardSlotDefinition extends ReasoningBoardSlotBaseDefinition {
  type: 'single_card';
  candidateCardIds: string[];
  answerCardIds: string[];
  candidateConnectionIds?: never;
  answerConnectionIds?: never;
}

export interface ReasoningBoardOrderedCardsSlotDefinition extends ReasoningBoardSlotBaseDefinition {
  type: 'ordered_cards';
  candidateCardIds: string[];
  answerCardIds: string[];
  candidateConnectionIds?: never;
  answerConnectionIds?: never;
}

export interface ReasoningBoardConnectionSlotDefinition extends ReasoningBoardSlotBaseDefinition {
  type: 'connection';
  candidateConnectionIds: string[];
  answerConnectionIds: string[];
  candidateCardIds?: never;
  answerCardIds?: never;
}

export type ReasoningBoardSlotDefinition =
  | ReasoningBoardSingleCardSlotDefinition
  | ReasoningBoardOrderedCardsSlotDefinition
  | ReasoningBoardConnectionSlotDefinition;

export interface ReasoningBoardEvaluationDefinition {
  maxScore: number;
  partialThreshold: number;
  truthSuspectedThreshold: number;
  scoreLabels: string[];
}

export interface ReasoningBoardCompletionDefinition {
  minimumRequiredSlots: number;
  requiredSlotIds: string[];
  onComplete: ReasoningBoardCompletionAction;
  summaryText: string;
  caseStatusPolicy: ReasoningBoardCaseStatusPolicy;
}

export interface ReasoningBoardDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  entryNodeId: string;
  returnNodeId: string;
  ui: ReasoningBoardUiDefinition;
  cards: ReasoningBoardCardDefinition[];
  connections: ReasoningBoardConnectionDefinition[];
  slots: ReasoningBoardSlotDefinition[];
  evaluation: ReasoningBoardEvaluationDefinition;
  completion: ReasoningBoardCompletionDefinition;
}

export interface TownAnxietyUiDefinition {
  layout: TownAnxietyUiLayout;
  showTrendIcon: boolean;
  showBulletinTicker: boolean;
  showResidentLine: boolean;
}

export interface TownAnxietyThresholdDefinition {
  id: string;
  minLevel: number;
  label: string;
  tone: TownAnxietyTone;
  backgroundId: string;
  residentCharacterId: string;
  residentLine: string;
  bulletinText: string;
  uiColor: string;
  displayOrder: number;
  tags?: string[];
}

export type TownAnxietyTriggerDefinition =
  | {
      type: TownAnxietyReferenceTriggerType;
      id: string;
      status?: never;
    }
  | {
      type: 'case_status';
      id: string;
      status: CaseStatus;
    }
  | {
      type: 'manual';
      id?: never;
      status?: never;
    };

export interface TownAnxietyEventDefinition {
  id: string;
  trigger: TownAnxietyTriggerDefinition;
  delta: number;
  label: string;
  description: string;
  feedbackText: string;
  applyOnce: boolean;
  displayOrder: number;
  tags?: string[];
}

export interface TownAnxietyCompletionDefinition {
  storagePolicy: TownAnxietyStoragePolicy;
  affectsTestimony: boolean;
  affectsBackground: boolean;
  affectsBulletin: boolean;
}

export interface TownAnxietyDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  maxLevel: number;
  initialLevel: number;
  ui: TownAnxietyUiDefinition;
  thresholds: TownAnxietyThresholdDefinition[];
  events: TownAnxietyEventDefinition[];
  completion: TownAnxietyCompletionDefinition;
}

export interface UnresolvedLogUnlockDefinition {
  caseStatus: CaseStatus;
  afterNodeId: string;
}

export interface UnresolvedLogUiDefinition {
  layout: UnresolvedLogUiLayout;
  enableTagFilter: boolean;
  enableCharacterFilter: boolean;
  enableRelatedCaseLinks: boolean;
  showKonaiDeductionLog: boolean;
}

export interface UnresolvedLogTimelineItemDefinition {
  id: string;
  label: string;
  timeLabel: string;
  type: UnresolvedLogTimelineType;
  sceneNodeId?: string;
  summary: string;
  relatedEvidenceIds?: string[];
  relatedCharacterIds?: string[];
  relatedTestimonyStatementIds?: string[];
  relatedReasoningSlotIds?: string[];
  relatedTownAnxietyEventIds?: string[];
  tags: string[];
  displayOrder: number;
}

export interface UnresolvedLogTagFilterDefinition {
  id: string;
  label: string;
  tags: string[];
  displayOrder: number;
}

export interface UnresolvedLogCharacterFilterDefinition {
  characterId: string;
  label: string;
  displayOrder: number;
}

export interface UnresolvedLogRelatedCaseDefinition {
  caseId: string;
  relation: UnresolvedLogRelatedCaseRelation;
  description: string;
  displayOrder: number;
}

export interface UnresolvedKonaiDeductionLogDefinition {
  id: string;
  deductionStepId: string;
  claimSummary: string;
  observationKept: string;
  whyWrong: string;
  relatedEvidenceIds: string[];
  relatedCharacterIds?: string[];
  tags: string[];
  displayOrder: number;
}

export interface UnresolvedLogCompletionDefinition {
  caseStatusPolicy: UnresolvedLogCaseStatusPolicy;
  showUnresolvedStamp: boolean;
  defaultSort: UnresolvedLogDefaultSort;
}

export interface UnresolvedLogDefinition {
  id: string;
  caseId: string;
  title: string;
  summary?: string;
  unlock: UnresolvedLogUnlockDefinition;
  ui: UnresolvedLogUiDefinition;
  timeline: UnresolvedLogTimelineItemDefinition[];
  tagFilters: UnresolvedLogTagFilterDefinition[];
  characterFilters: UnresolvedLogCharacterFilterDefinition[];
  relatedCases: UnresolvedLogRelatedCaseDefinition[];
  konaiDeductionLogs: UnresolvedKonaiDeductionLogDefinition[];
  completion: UnresolvedLogCompletionDefinition;
}

export interface GameConfigData {
  projectTitle: string;
  projectSubtitle: string;
  projectFullTitle: string;
  developmentTitle: boolean;
  baseResolution: {
    width: number;
    height: number;
  };
  initialScenarioId: string;
  initialCaseId: string;
  saveSlotCount: number;
  storageKeys: {
    saveData: string;
    settings: string;
  };
}

export interface GameSettings {
  masterVolume: number;
  bgmVolume: number;
  seVolume: number;
  voiceVolume: number;
  textSpeed: number;
  autoSpeed: number;
  skipReadText: boolean;
  reduceMotion: boolean;
  lightweightEffects: boolean;
  screenShake: boolean;
  visualEffects: boolean;
}

export interface ScenarioResumePoint {
  scenarioId: string;
  nodeId: string;
  lineId?: string;
  lineIndex: number;
}

export interface BacklogEntry {
  id: string;
  scenarioId: string;
  nodeId: string;
  lineId?: string;
  lineIndex: number;
  speaker?: string;
  speakerId?: string;
  text: string;
}

export interface GameProgress {
  schemaVersion: number;
  currentScenarioId: string;
  currentCaseId: string;
  resumePoint: ScenarioResumePoint;
  unlockedCaseIds: string[];
  readNodeIds: string[];
  readLineIds: string[];
  readLineIdsByCase: Record<string, string[]>;
  backlog: BacklogEntry[];
  evidenceIds: string[];
  evidenceIdsByCase: Record<string, string[]>;
  flagsByCase: Record<string, Record<string, boolean | number | string>>;
  caseStatuses: Record<string, CaseStatus>;
  audioState: {
    currentBgmId: string | null;
  };
  lastSceneType: string;
}

export interface GameStateSnapshot {
  progress: GameProgress;
  settings: GameSettings;
}

export interface GameProgressHydration {
  schemaVersion?: unknown;
  currentScenarioId?: unknown;
  currentCaseId?: unknown;
  resumePoint?: unknown;
  unlockedCaseIds?: unknown;
  readNodeIds?: unknown;
  readLineIds?: unknown;
  readLineIdsByCase?: unknown;
  backlog?: unknown;
  evidenceIds?: unknown;
  evidenceIdsByCase?: unknown;
  flagsByCase?: unknown;
  caseStatuses?: unknown;
  audioState?: unknown;
  lastSceneType?: unknown;
}

export interface GameSettingsHydration {
  masterVolume?: unknown;
  bgmVolume?: unknown;
  seVolume?: unknown;
  voiceVolume?: unknown;
  textSpeed?: unknown;
  autoSpeed?: unknown;
  skipReadText?: unknown;
  reduceMotion?: unknown;
  lightweightEffects?: unknown;
  screenShake?: unknown;
  visualEffects?: unknown;
}

export interface GameStateHydration {
  progress?: GameProgressHydration;
  settings?: GameSettingsHydration;
}
